import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Upload, Edit, Trash2, LogOut, RefreshCw, Wand2, Loader2, FolderPlus, ChevronDown, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import DragDropUpload from "../components/DragDropUpload";
import AdminSubNav from "../components/AdminSubNav";
import { isSuperadmin as checkSuperadmin, canManageOrg as checkCanManageOrg } from "../lib/permissions";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LANGUAGE_OPTIONS = ["en", "fr", "es", "de", "ru", "zh", "other"];
const LANGUAGE_LABELS = {
  en: "English",
  fr: "French",
  es: "Spanish",
  de: "German",
  ru: "Russian",
  zh: "Chinese",
  other: "Other",
};
const CATEGORY_LABELS = {
  new: "New",
  article: "Article/Paper",
  thesis: "Thesis",
  course: "Course",
  report: "Report",
  book: "Book",
  note: "Note",
  preprint: "Preprint",
};
const PAGE_SIZE = 20;

const normalizeText = (value) =>
  (value || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

// Highlight search term in snippet text
const HighlightSnippet = ({ text, term }) => {
  if (!text || !term) return <span>{text}</span>;
  const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  return (
    <span>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark key={i} className="bg-yellow-200 text-yellow-900 px-0.5 rounded font-medium not-italic">{part}</mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </span>
  );
};



const normalizeOrg = (org) => {
  const raw = (org || "").trim();
  if (!raw) return "Public";
  if (raw === "PublicPublic") return "Public";
  if (raw.toLowerCase() === "public") return "Public";
  return raw;
};

const AdminItems = ({ user, setUser }) => {
  const [items, setItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  // Map of pageNum → next_cursor for that page (so we can skip-free navigate adjacent pages)
  const pageCursorMap = useRef({}); // { 1: 'oid', 2: 'oid', ... }
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkCategoryDialog, setShowBulkCategoryDialog] = useState(false);
  const [bulkCategoryValue, setBulkCategoryValue] = useState("new");
  const [bulkCategoryCustomMode, setBulkCategoryCustomMode] = useState(false);
  const [bulkCategorySaving, setBulkCategorySaving] = useState(false);
  const [checkingSimilarityId, setCheckingSimilarityId] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [extractingMeta, setExtractingMeta] = useState(false);
  const [bulkExtracting, setBulkExtracting] = useState(false);
  const [partialCount, setPartialCount] = useState(0);
  const [extractJob, setExtractJob] = useState(null);
  const [ocrQueueStatus, setOcrQueueStatus] = useState(null);
  const [enqueuingOcr, setEnqueuingOcr] = useState(false);
  const [reextracting, setReextracting] = useState(false);
  const [convertingToPdf, setConvertingToPdf] = useState(false);
  const [convertPdfStatus, setConvertPdfStatus] = useState(null);
  // Duplicate-finder dialog state
  const [showDuplicatesDialog, setShowDuplicatesDialog] = useState(false);
  const [dupesMode, setDupesMode] = useState("checksum"); // 'checksum' | 'near'
  const [dupesLoading, setDupesLoading] = useState(false);
  const [dupesClusters, setDupesClusters] = useState([]);
  const [dupesTotal, setDupesTotal] = useState(0);
  const [dupesScanned, setDupesScanned] = useState(0);
  const [dupesThreshold, setDupesThreshold] = useState(0.85);
  const [dupesSelected, setDupesSelected] = useState({}); // {item_id: true}
  const [dupesDeleting, setDupesDeleting] = useState(false);
  const [dupesMergeBeforeDelete, setDupesMergeBeforeDelete] = useState(true);
  // Broken-files dialog state
  const [showBrokenDialog, setShowBrokenDialog] = useState(false);
  const [brokenLoading, setBrokenLoading] = useState(false);
  const [brokenItems, setBrokenItems] = useState([]);
  const [brokenSelected, setBrokenSelected] = useState({});
  const [brokenDeleting, setBrokenDeleting] = useState(false);
  const [brokenMinSize, setBrokenMinSize] = useState(500);
  const [brokenReuploading, setBrokenReuploading] = useState(false);
  const brokenReuploadInputRef = useRef(null);
  // Diagnose-duplicate dialog state
  const [showDiagnoseDialog, setShowDiagnoseDialog] = useState(false);
  const [diagnoseTitle, setDiagnoseTitle] = useState("");
  const [diagnoseLoading, setDiagnoseLoading] = useState(false);
  const [diagnoseResult, setDiagnoseResult] = useState(null);
  const [diagnoseError, setDiagnoseError] = useState("");
  // Ghost-cleanup state
  const [purgingGhosts, setPurgingGhosts] = useState(false);
  // Add-to-collection dialog state
  const [collectionDialogOpen, setCollectionDialogOpen] = useState(false);
  const [collectionDialogItemIds, setCollectionDialogItemIds] = useState([]);
  const [myCollections, setMyCollections] = useState([]);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [addingToCollection, setAddingToCollection] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState("");
  const [creatingCollection, setCreatingCollection] = useState(false);
  const [showNewCollectionInput, setShowNewCollectionInput] = useState(false);
  // Ticker that re-renders every 5s so the 60-second "Just extracted" highlight
  // expires automatically without needing a page reload.
  const [, setHighlightTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setHighlightTick((n) => n + 1), 5000);
    return () => clearInterval(id);
  }, []);
  const [itemSearch, setItemSearch] = useState("");
  const [similarityFilter, setSimilarityFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [orgFilter, setOrgFilter] = useState("all");
  const [orgOptions, setOrgOptions] = useState([]);
  const [languageFilter, setLanguageFilter] = useState("all");
  const [fileTypeFilter, setFileTypeFilter] = useState("all");
  const [yearFrom, setYearFrom] = useState("");
  const [yearTo, setYearTo] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [availableFilters, setAvailableFilters] = useState({ languages: [], file_types: [], categories: [] });
  const STANDARD_CATEGORIES = ["new", "article", "thesis", "course", "report", "book", "note", "preprint"];
  const customCategories = (availableFilters.categories || []).filter(
    (c) => c && !STANDARD_CATEGORIES.includes(c.toLowerCase())
  );
  const [customCategoryMode, setCustomCategoryMode] = useState(false);

  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({
    title: "",
    authors: "",
    abstract: "",
    year: "",
    language: "en",
    category: "new",
    tags: "",
    access_level: "public",
    is_paid: false,
    price: "",
    organization: "Public",
  });

  const navigate = useNavigate();
  const isSuperadmin = checkSuperadmin(user);
  const canManageOrg = checkCanManageOrg(user);

  const pendingScrollRef = useRef(null);
  const searchTimerRef = useRef(null);

  // Debounced search — waits 400ms after last keystroke before firing API call
  const debouncedSearch = useCallback((val) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      fetchItems({ page: 1, search: val });
    }, 400);
  }, []);

  // Restore admin items UI state (page, filters, scroll) when returning via Back
  useEffect(() => {
    const restoreStateAndFetch = async () => {
      let restored = false;
      try {
        const raw = typeof window !== "undefined" ? sessionStorage.getItem("admin_items_state") : null;
        if (raw) {
          const parsed = JSON.parse(raw);
          if (typeof parsed.page === "number") {
            setPage(parsed.page);
          }
          if (typeof parsed.itemSearch === "string") {
            setItemSearch(parsed.itemSearch);
          }
          if (typeof parsed.categoryFilter === "string") {
            setCategoryFilter(parsed.categoryFilter);
          }
          if (typeof parsed.orgFilter === "string") {
            setOrgFilter(parsed.orgFilter);
          }
          if (typeof parsed.similarityFilter === "string") {
            setSimilarityFilter(parsed.similarityFilter);
          }
          if (typeof parsed.sortBy === "string") {
            setSortBy(parsed.sortBy);
          }
          if (typeof parsed.sortOrder === "string") {
            setSortOrder(parsed.sortOrder);
          }

          await fetchItems({
            page: parsed.page || 1,
            category: parsed.categoryFilter ?? categoryFilter,
            organization: parsed.orgFilter ?? orgFilter,
            search: parsed.itemSearch ?? itemSearch,
          });

          // Defer scroll restoration so React has time to render the fetched items
          if (typeof parsed.scrollY === "number" && parsed.scrollY > 0) {
            pendingScrollRef.current = parsed.scrollY;
          }

          restored = true;
        }
      } catch (e) {
        console.error("Failed to restore admin items UI state", e);
      }

      if (!restored) {
        await fetchItems();
      }

      if (canManageOrg) {
        loadOrgOptions();
      }
    };

    restoreStateAndFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Execute deferred scroll restoration after items render
  useEffect(() => {
    if (pendingScrollRef.current != null && !loading && items.length > 0) {
      requestAnimationFrame(() => {
        window.scrollTo({ top: pendingScrollRef.current, behavior: "auto" });
        pendingScrollRef.current = null;
      });
    }
  }, [loading, items]);

  // Track scroll position continuously so it's always up-to-date
  const scrollYRef = useRef(0);
  useEffect(() => {
    const onScroll = () => { scrollYRef.current = window.scrollY; };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Poll OCR queue status every 15s while the page is open
  useEffect(() => {
    fetchOcrQueueStatus();
    fetchConvertPdfStatus();
    const t = setInterval(fetchOcrQueueStatus, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist admin items UI state when leaving the page
  useEffect(() => {
    return () => {
      try {
        const state = {
          page,
          itemSearch,
          categoryFilter,
          orgFilter,
          similarityFilter,
          sortBy,
          sortOrder,
          scrollY: scrollYRef.current,
        };
        sessionStorage.setItem("admin_items_state", JSON.stringify(state));
      } catch (e) {
        console.error("Failed to save admin items UI state", e);
      }
    };
  }, [page, itemSearch, categoryFilter, orgFilter, similarityFilter, sortBy, sortOrder]);

  const abortRef = useRef(null);

  const fetchItems = async (options = {}) => {
    // Cancel any in-flight request
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      setLoading(true);
      const token = localStorage.getItem("access_token");
      const params = new URLSearchParams();

      const nextPage = options.page || page;
      const nextCategory = options.category ?? categoryFilter;
      const nextOrg = options.organization ?? orgFilter;
      const nextSearch = options.search ?? itemSearch;
      const nextLang = options.language ?? languageFilter;
      const nextFileType = options.file_type ?? fileTypeFilter;
      const nextYearFrom = options.year_from ?? yearFrom;
      const nextYearTo = options.year_to ?? yearTo;
      const nextPriceMin = options.price_min ?? priceMin;
      const nextPriceMax = options.price_max ?? priceMax;

      params.set("per_page", String(PAGE_SIZE));
      params.set("page", String(nextPage));

      // O(1) deep pagination: if we know the cursor for the previous page and
      // we're moving sequentially forward, send it. Server uses cursor (no skip).
      // Falling back to skip-based for jumps to arbitrary pages is fine — indexes
      // make even page 100 fast.
      const cursorForThisPage = pageCursorMap.current[nextPage - 1];
      if (cursorForThisPage && nextPage > 1 && nextPage > page) {
        params.set("cursor", cursorForThisPage);
      } else if (nextPage === 1) {
        // Reset cursor cache when going back to page 1 (filters may have changed)
        pageCursorMap.current = {};
      }

      const term = (nextSearch || "").trim();
      const nextSortBy = options.sort_by ?? sortBy;
      const nextSortOrder = options.sort_order ?? sortOrder;

      // When searching, sort by relevance so title matches come first
      if (term) {
        params.set("sort_by", "relevance");
        params.set("sort_order", "desc");
      } else {
        params.set("sort_by", nextSortBy || "created_at");
        params.set("sort_order", nextSortOrder || "desc");
      }

      if (term) params.set("q", term);
      if (nextCategory !== "all") params.set("category", nextCategory);
      if (canManageOrg && nextOrg !== "all") params.set("organization", nextOrg);
      if (nextLang !== "all") params.set("language", nextLang);
      if (nextFileType !== "all") params.set("file_type", nextFileType);
      if (nextYearFrom) params.set("year_from", nextYearFrom);
      if (nextYearTo) params.set("year_to", nextYearTo);
      if (nextPriceMin) params.set("price_min", nextPriceMin);
      if (nextPriceMax) params.set("price_max", nextPriceMax);

      const url = `${API}/smart-search?${params.toString()}`;
      const config = {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
        timeout: 30000,
      };

      // Retry once on failure (production Atlas can be slow on deep pagination)
      let response;
      try {
        response = await axios.get(url, config);
      } catch (firstErr) {
        if (axios.isCancel(firstErr) || firstErr.name === "CanceledError" || firstErr.code === "ERR_CANCELED") throw firstErr;
        // Retry once
        response = await axios.get(url, { ...config, timeout: 45000 });
      }

      const data = response.data || {};
      setItems(data.items || []);
      setTotalItems(data.total || 0);
      setPage(nextPage);
      // Cache the next_cursor so the next page can be requested via cursor (O(1))
      if (data.next_cursor) {
        pageCursorMap.current[nextPage] = data.next_cursor;
      }
      if (data.filters_available) {
        setAvailableFilters(data.filters_available);
      }
    } catch (error) {
      // Don't show error for cancelled requests (user typed more)
      if (axios.isCancel(error) || error.name === "CanceledError" || error.code === "ERR_CANCELED") return;
      console.error("Fetch items error:", error);
      toast.error("Error loading items. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const loadOrgOptions = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.get(`${API}/admin/organizations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const names = (resp.data || []).map((o) => o.organization || o.name).filter(Boolean);
      setOrgOptions(names);
    } catch (err) {
      console.error("Org options load error:", err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    setUser(null);
    navigate("/");
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm("Move this item to the recycle bin for 60 days?")) return;

    try {
      const token = localStorage.getItem("access_token");
      await axios.delete(`${API}/admin/items/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Item moved to recycle bin");
      fetchItems();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Error deleting item");
    }
  };

  const handleToggleSelectAll = () => {
    const pageIds = pagedItems.map((it) => it.id);
    const allSelected = pageIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      const next = new Set(selectedIds);
      pageIds.forEach((id) => next.add(id));
      setSelectedIds(Array.from(next));
    }
  };

  const handleToggleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`Move ${selectedIds.length} item(s) to the recycle bin for 60 days?`)) return;
    try {
      const token = localStorage.getItem("access_token");
      await axios.delete(`${API}/admin/items/bulk`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { ids: selectedIds },
      });
      toast.success("Selected items moved to recycle bin");
      setSelectedIds([]);
      fetchItems();
    } catch (error) {
      console.error("Bulk delete error:", error);
      const detail = error.response?.data?.detail || "Error deleting selected items";
      toast.error(detail);
    }
  };

  const applyBulkCategory = async () => {
    const category = (bulkCategoryValue || "").trim();
    if (!category || !selectedIds.length || bulkCategorySaving) return;
    setBulkCategorySaving(true);
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.post(
        `${API}/admin/items/bulk-category`,
        { ids: selectedIds, category },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(
        `${resp.data.updated} item(s) moved to "${category}"${resp.data.skipped ? `, ${resp.data.skipped} skipped` : ""}`,
      );
      setShowBulkCategoryDialog(false);
      setBulkCategoryCustomMode(false);
      setSelectedIds([]);
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bulk category change failed");
    } finally {
      setBulkCategorySaving(false);
    }
  };

  const runSimilarityCheck = async (item) => {
    setCheckingSimilarityId(item.id);
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(`${API}/admin/items/${item.id}/similarity`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000,
      });
      const { similarity, matched_item_id, matched_title, matched_authors, matched_year } = response.data || {};
      const percent = Math.round((similarity || 0) * 100);
      const isHigh = percent >= 30;

      if (matched_item_id) {
        const details = [
          matched_title || `ID: ${matched_item_id}`,
          matched_authors && matched_authors.length ? `Authors: ${matched_authors.join(", ")}` : null,
          matched_year ? `Year: ${matched_year}` : null,
        ]
          .filter(Boolean)
          .join(" \n");
        const msg = `Highest similarity: ${percent}%\n${details}`;
        toast[isHigh ? "error" : "info"](msg, {
          duration: isHigh ? 8000 : 5000,
        });

        // Navigate to dedicated similarity comparison page with context
        navigate(`/admin/items/${item.id}/similarity`, {
          state: {
            auditedItem: item,
            similarity: {
              percent,
              matched_item_id,
            },
          },
        });
      } else {
        const msg = `Similarity score: ${percent}%`;
        toast[isHigh ? "error" : "info"](msg, {
          duration: isHigh ? 8000 : 5000,
        });
      }
    } catch (error) {
      console.error("Similarity check error:", error);
      // Extract the most informative error message possible
      let detail = "Error computing similarity";
      if (error.response?.data?.detail) {
        const raw = error.response.data.detail;
        // If the error is about AI OCR failing due to OpenAI issues, show a friendly message
        if (raw.includes("502") || raw.includes("503") || raw.includes("BadGateway")) {
          detail = "AI text extraction temporarily unavailable (OpenAI service issue). Please try again in a minute.";
        } else if (raw.includes("Could not extract text")) {
          // Show a shortened version — full detail in console
          const detailsIdx = raw.indexOf("Details:");
          detail = detailsIdx >= 0 ? raw.substring(0, detailsIdx + 100) + "..." : raw.substring(0, 200);
        } else {
          detail = raw.length > 200 ? raw.substring(0, 200) + "..." : raw;
        }
      } else if (error.response?.status) {
        detail = `Server error (${error.response.status}). Please try again.`;
      } else if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
        detail = "Request timed out. The file may be too large or the server is busy. Please try again.";
      } else if (error.message) {
        detail = `Network error: ${error.message}`;
      }
      toast.error(detail, { duration: 8000 });
    } finally {
      setCheckingSimilarityId(null);
    }
  };

  const runBackfillTokens = async () => {
    setBackfilling(true);
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.post(`${API}/admin/backfill-similarity-tokens`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000,
      });
      const { updated } = resp.data || {};
      if (updated > 0) {
        toast.success(`Optimized similarity data for ${updated} documents. Future checks will be faster!`, { duration: 8000 });
      } else {
        toast.info("All documents are already optimized.", { duration: 5000 });
      }
    } catch (error) {
      console.error("Backfill error:", error);
      toast.error("Error optimizing similarity data");
    } finally {
      setBackfilling(false);
    }
  };

  const runRepairFiles = async () => {
    setRepairing(true);
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.post(`${API}/admin/repair-broken-files`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000,
      });
      const { repaired } = resp.data || {};
      if (repaired > 0) {
        toast.success(`Fixed ${repaired} item(s) with missing files. They now show "Upload" button so you can re-upload.`, { duration: 8000 });
        fetchItems();
      } else {
        toast.info("All files are OK — no repairs needed.", { duration: 5000 });
      }
    } catch (error) {
      console.error("Repair error:", error);
      toast.error("Error running file repair");
    } finally {
      setRepairing(false);
    }
  };

  const fetchOcrQueueStatus = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.get(`${API}/admin/ocr-queue/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOcrQueueStatus(resp.data);
    } catch (err) {
      console.error("OCR queue status error:", err);
    }
  };

  const runEnqueueOcr = async () => {
    setEnqueuingOcr(true);
    try {
      const token = localStorage.getItem("access_token");
      // First: unstick any stale "processing" jobs from a previous worker crash
      try {
        await axios.post(
          `${API}/admin/ocr-queue/reset-stuck`,
          { stuck_minutes: 10 },
          { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 },
        );
      } catch (_) { /* best-effort */ }

      const resp = await axios.post(
        `${API}/admin/ocr-queue/enqueue-missing`,
        { limit: 500 },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 },
      );
      const { enqueued } = resp.data || {};
      if (enqueued > 0) {
        toast.success(`Queued ${enqueued} item(s) for AI OCR. They will be processed in the background.`, { duration: 8000 });
      } else {
        toast.info("No items need OCR — all eligible files already have text or are already queued.", { duration: 6000 });
      }
      fetchOcrQueueStatus();
    } catch (err) {
      console.error("Enqueue OCR error:", err);
      toast.error(err.response?.data?.detail || "Error queuing items for OCR");
    } finally {
      setEnqueuingOcr(false);
    }
  };

  const fetchConvertPdfStatus = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.get(`${API}/admin/items/convert-to-pdf-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConvertPdfStatus(resp.data);
    } catch (err) {
      console.error("Convert-to-PDF status error:", err);
    }
  };

  const runConvertToPdfBatch = async () => {
    // Refresh status first so the confirm message shows the real count
    let statusData = convertPdfStatus;
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.get(`${API}/admin/items/convert-to-pdf-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      statusData = resp.data;
      setConvertPdfStatus(statusData);
    } catch (_) { /* fall through */ }

    const remaining = statusData?.convertible ?? 0;
    if (remaining === 0) {
      toast.info("No non-PDF items to convert. All uploaded files are already PDF.", { duration: 6000 });
      return;
    }
    // Batch of 10 per click (backend caps at 25). Ask for confirmation.
    const batch = Math.min(remaining, 10);
    if (!window.confirm(
      `Convert the next ${batch} non-PDF item(s) to PDF? ` +
      `(${remaining} total remain.) This uses LibreOffice for Office docs, ` +
      `img2pdf for images, and WeasyPrint for HTML/EPUB. Each item takes 2-6s.`
    )) return;

    setConvertingToPdf(true);
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.post(
        `${API}/admin/items/convert-to-pdf-batch`,
        { auto: true, batch_size: batch },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 300000 },
      );
      const { converted, skipped, errors, processed } = resp.data || {};
      if (converted > 0) {
        toast.success(
          `Converted ${converted} item(s) to PDF (${skipped} skipped, ${errors} failed). ` +
          `${(remaining - converted).toFixed(0)} remaining — click again to continue.`,
          { duration: 8000 },
        );
      } else if (processed > 0) {
        toast.warning(
          `${processed} item(s) processed but none converted (${skipped} skipped, ${errors} failed). ` +
          `Check server logs for details.`,
          { duration: 8000 },
        );
      } else {
        toast.info("No items needed conversion.", { duration: 5000 });
      }
      fetchConvertPdfStatus();
      fetchItems();
    } catch (err) {
      console.error("Convert to PDF batch error:", err);
      toast.error(err.response?.data?.detail || "Error converting items to PDF");
    } finally {
      setConvertingToPdf(false);
    }
  };

  const openAddToCollection = async (itemIds) => {
    if (!itemIds || itemIds.length === 0) return;
    setCollectionDialogItemIds(itemIds);
    setCollectionDialogOpen(true);
    setCollectionsLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.get(`${API}/collections/my`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMyCollections(resp.data || []);
    } catch (err) {
      console.error("Failed to load collections:", err);
      toast.error("Failed to load your collections");
      setMyCollections([]);
    } finally {
      setCollectionsLoading(false);
    }
  };

  const addItemsToCollection = async (collectionId) => {
    setAddingToCollection(true);
    try {
      const token = localStorage.getItem("access_token");
      let added = 0;
      let alreadyIn = 0;
      let failed = 0;
      for (const itemId of collectionDialogItemIds) {
        try {
          const r = await axios.post(
            `${API}/collections/${collectionId}/items/${itemId}`,
            {},
            { headers: { Authorization: `Bearer ${token}` } },
          );
          if (r.data?.message?.includes("already")) alreadyIn += 1;
          else added += 1;
        } catch (e) {
          failed += 1;
        }
      }
      const parts = [];
      if (added) parts.push(`${added} added`);
      if (alreadyIn) parts.push(`${alreadyIn} already in`);
      if (failed) parts.push(`${failed} failed`);
      toast.success(parts.join(", ") || "Done");
      setCollectionDialogOpen(false);
      setCollectionDialogItemIds([]);
      if (collectionDialogItemIds.length > 1) setSelectedIds([]);
    } catch (err) {
      toast.error("Error adding to collection");
    } finally {
      setAddingToCollection(false);
    }
  };

  const createAndAddToCollection = async () => {
    const name = newCollectionName.trim();
    if (!name) {
      toast.error("Collection name is required");
      return;
    }
    setCreatingCollection(true);
    try {
      const token = localStorage.getItem("access_token");
      // Create the collection
      const created = await axios.post(
        `${API}/collections`,
        { name, description: "", is_public: false },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const newColl = created.data;
      // Add it to the local list so subsequent navigations show it
      setMyCollections((prev) => [newColl, ...prev]);
      // Immediately add the selected items into it
      await addItemsToCollection(newColl.id);
      // Reset the input
      setNewCollectionName("");
      setShowNewCollectionInput(false);
    } catch (err) {
      console.error("Create collection error:", err);
      toast.error(err.response?.data?.detail || "Failed to create collection");
    } finally {
      setCreatingCollection(false);
    }
  };

  const runReextractFilenameTitles = async () => {
    setReextracting(true);
    try {
      const token = localStorage.getItem("access_token");
      // First a dry-run to show how many items will be touched
      const dry = await axios.post(
        `${API}/admin/bulk-reextract-filename-titles`,
        { dry_run: true, limit: 200 },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 },
      );
      const count = dry.data?.count || 0;
      if (count === 0) {
        toast.info("No items found with filename-like titles + extracted text.");
        return;
      }
      if (!window.confirm(`Re-extract metadata for ${count} item(s) whose title looks like a filename (e.g. '300 000 060 590 (1)')?\n\nThis may take ${Math.ceil(count * 0.25)} minute(s).`)) {
        return;
      }
      const resp = await axios.post(
        `${API}/admin/bulk-reextract-filename-titles`,
        { limit: 200 },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 },
      );
      const { scheduled, background, processed, total, failed } = resp.data || {};
      if (background) {
        toast.success(
          `Started background re-extraction for ${scheduled} item(s). They'll fill in over the next ${Math.ceil(scheduled * 15 / 60)} minute(s) — refresh later to see progress.`,
          { duration: 12000 },
        );
      } else {
        toast.success(`Re-extracted ${processed}/${total} items${failed > 0 ? ` (${failed} failed)` : ""}.`, { duration: 10000 });
      }
      fetchItems();
    } catch (err) {
      console.error("Re-extract error:", err);
      toast.error(err.response?.data?.detail || "Error re-extracting metadata");
    } finally {
      setReextracting(false);
    }
  };

  const loadDuplicateClusters = useCallback(async (mode, threshold) => {
    setDupesLoading(true);
    setDupesSelected({});
    setDupesClusters([]);
    setDupesScanned(0);
    try {
      const token = localStorage.getItem("access_token");
      const url = mode === "near"
        ? `${API}/admin/items/near-duplicates?limit=5000&threshold=${threshold ?? 0.85}`
        : `${API}/admin/items/checksum-duplicates?limit=200`;
      const resp = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000,
      });
      const clusters = resp.data?.clusters || [];
      setDupesClusters(clusters);
      setDupesTotal(resp.data?.total_duplicate_items || 0);
      setDupesScanned(resp.data?.scanned || 0);

      // Pre-select all non-oldest items (safe default for both modes)
      const preselect = {};
      for (const c of clusters) {
        c.items.slice(1).forEach((it) => { preselect[it.item_id] = true; });
      }
      setDupesSelected(preselect);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load duplicates");
    } finally {
      setDupesLoading(false);
    }
  }, []);

  const deleteSelectedDuplicates = async () => {
    // Build cluster-aware payload: {keeper_id, dupe_ids[]} per cluster
    const cls = [];
    let totalDupes = 0;
    for (const c of dupesClusters) {
      const keeper = c.items[0];
      const dupe_ids = c.items
        .slice(1)
        .filter((it) => dupesSelected[it.item_id])
        .map((it) => it.item_id);
      if (dupe_ids.length > 0) {
        cls.push({ keeper_id: keeper.item_id, dupe_ids });
        totalDupes += dupe_ids.length;
      }
    }
    if (totalDupes === 0) {
      toast.info("Select at least one duplicate to delete.");
      return;
    }
    const action = dupesMergeBeforeDelete
      ? `Merge metadata from ${totalDupes} duplicate(s) into the kept items, then move them to the Recycle Bin?`
      : `Move ${totalDupes} duplicate(s) to the Recycle Bin (without merging metadata)?`;
    if (!window.confirm(`${action}\n\nYou can restore items within 30 days.`)) {
      return;
    }
    setDupesDeleting(true);
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.post(
        `${API}/admin/items/merge-duplicates`,
        { clusters: cls, merge: dupesMergeBeforeDelete },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 },
      );
      const { merged, deleted, fields_filled } = resp.data || {};
      if (dupesMergeBeforeDelete) {
        const filled = Object.entries(fields_filled || {})
          .map(([k, v]) => `${k}×${v}`)
          .join(", ");
        toast.success(
          `Merged ${merged} cluster(s) (filled: ${filled || "no gaps"}), moved ${deleted} item(s) to Recycle Bin`,
          { duration: 8000 },
        );
      } else {
        toast.success(`Moved ${deleted} duplicate(s) to Recycle Bin`);
      }
      await loadDuplicateClusters(dupesMode, dupesThreshold);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
    } finally {
      setDupesDeleting(false);
    }
  };

  const loadBrokenFiles = useCallback(async (minSize) => {
    setBrokenLoading(true);
    setBrokenSelected({});
    setBrokenItems([]);
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.get(
        `${API}/admin/items/broken-files?limit=2000&min_size_bytes=${minSize ?? 500}`,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 90000 },
      );
      const items = resp.data?.items || [];
      setBrokenItems(items);
      const sel = {};
      items.forEach((it) => { sel[it.item_id] = true; });
      setBrokenSelected(sel);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to scan for broken files");
    } finally {
      setBrokenLoading(false);
    }
  }, []);

  const deleteBrokenFiles = async () => {
    const ids = Object.keys(brokenSelected).filter((id) => brokenSelected[id]);
    if (ids.length === 0) {
      toast.info("Select at least one item.");
      return;
    }
    if (!window.confirm(`Move ${ids.length} broken/empty item(s) to the Recycle Bin? You can restore them within 30 days.`)) return;
    setBrokenDeleting(true);
    try {
      const token = localStorage.getItem("access_token");
      await axios.delete(`${API}/admin/items/bulk`, {
        data: { ids },
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Moved ${ids.length} broken item(s) to Recycle Bin`);
      await loadBrokenFiles(brokenMinSize);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBrokenDeleting(false);
    }
  };

  const runDiagnoseDuplicate = async () => {
    const raw = diagnoseTitle.trim();
    if (!raw) {
      toast.info("Enter a filename or title to diagnose.");
      return;
    }
    // Normalize like the upload flow: strip extension + replace _- with spaces
    const normalized = raw.replace(/\.[^/.]+$/, "").replace(/[_-]/g, " ");
    setDiagnoseLoading(true);
    setDiagnoseResult(null);
    setDiagnoseError("");
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.get(
        `${API}/admin/items/diagnose-upload?title=${encodeURIComponent(normalized)}`,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 },
      );
      setDiagnoseResult({ ...resp.data, normalized, mode: "title" });
    } catch (err) {
      setDiagnoseError(err.response?.data?.detail || err.message || "Diagnose failed");
    } finally {
      setDiagnoseLoading(false);
    }
  };

  const runDiagnoseDuplicateByContent = async (file) => {
    if (!file) return;
    setDiagnoseLoading(true);
    setDiagnoseResult(null);
    setDiagnoseError("");
    try {
      const token = localStorage.getItem("access_token");
      const formData = new FormData();
      formData.append("file", file);
      const resp = await axios.post(
        `${API}/admin/items/diagnose-upload-content`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
          timeout: 60000,
        },
      );
      setDiagnoseResult({ ...resp.data, mode: "content" });
    } catch (err) {
      setDiagnoseError(err.response?.data?.detail || err.message || "Diagnose failed");
    } finally {
      setDiagnoseLoading(false);
    }
  };

  const deleteBlockerItem = async (itemId) => {
    if (!window.confirm("Move this blocking item to the Recycle Bin? You'll then be able to upload the new file.")) return;
    try {
      const token = localStorage.getItem("access_token");
      await axios.delete(`${API}/admin/items/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Blocker moved to Recycle Bin. Try uploading again.");
      // Re-run diagnose so the result reflects the new state
      runDiagnoseDuplicate();
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete blocker");
    }
  };

  const purgeBlockerCompletely = async (itemId) => {
    if (!window.confirm("Permanently delete this item AND its file/text rows? This cannot be undone.")) return;
    try {
      const token = localStorage.getItem("access_token");
      // First soft-delete if it's still active
      await axios.delete(`${API}/admin/items/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
      // Then permanent-delete from recycle bin
      await axios.delete(`${API}/admin/recycle-bin/items/permanent`, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        data: { ids: [itemId] },
      });
      toast.success("Item permanently purged. Try uploading again.");
      runDiagnoseDuplicate();
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to purge item");
    }
  };

  // Normalize a string for filename↔title matching: lowercase, strip extension,
  // remove non-alphanumeric chars. So "Test_Article-v2.pdf" ≡ "test article v2"
  const _normalize = (s) => (s || "")
    .toLowerCase()
    .replace(/\.[a-z0-9]+$/, "")
    .replace(/[^a-z0-9]+/g, "");

  const openReuploadPicker = () => {
    const selectedCount = Object.values(brokenSelected).filter(Boolean).length;
    if (selectedCount === 0) {
      toast.info("Select at least one broken item to re-upload to.");
      return;
    }
    if (brokenReuploadInputRef.current) brokenReuploadInputRef.current.click();
  };

  const handleReuploadFiles = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (picked.length === 0) return;

    const targets = brokenItems.filter((it) => brokenSelected[it.item_id]);
    if (targets.length === 0) return;

    // Build a map: normalized title → broken item
    const byKey = new Map();
    for (const t of targets) byKey.set(_normalize(t.title), t);

    // Match each picked file
    const matches = []; // {file, item}
    const unmatched = [];
    for (const f of picked) {
      const key = _normalize(f.name);
      const target = byKey.get(key);
      if (target) {
        matches.push({ file: f, item: target });
        byKey.delete(key); // one file per item
      } else {
        unmatched.push(f.name);
      }
    }
    if (matches.length === 0) {
      toast.error("No filenames matched any selected broken item titles.");
      return;
    }
    if (unmatched.length > 0) {
      toast.info(`${unmatched.length} file(s) didn't match any selected item title.`);
    }

    setBrokenReuploading(true);
    const token = localStorage.getItem("access_token");
    let success = 0;
    let failed = 0;
    for (const { file, item } of matches) {
      try {
        const form = new FormData();
        form.append("file", file);
        await axios.post(
          `${API}/admin/items/${item.item_id}/replace-file`,
          form,
          {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 120000,
          },
        );
        success++;
      } catch (err) {
        console.error("Re-upload failed for", item.item_id, err);
        failed++;
      }
    }
    setBrokenReuploading(false);
    toast[failed === 0 ? "success" : "error"](
      `Re-uploaded ${success} file(s)${failed > 0 ? `, ${failed} failed` : ""}`,
    );
    await loadBrokenFiles(brokenMinSize);
    fetchItems();
  };


  const exportBrokenAsCSV = () => {
    if (brokenItems.length === 0) {
      toast.info("Nothing to export.");
      return;
    }
    const escape = (v) => {
      const s = v === null || v === undefined ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const cols = ["item_id", "title", "reason", "file_size", "filename", "organization", "category", "created_at"];
    const rows = [cols.join(",")];
    for (const it of brokenItems) {
      rows.push(cols.map((c) => escape(it[c])).join(","));
    }
    const csv = "\ufeff" + rows.join("\n"); // BOM so Excel opens UTF-8 cleanly
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const ts = new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-");
    a.href = url;
    a.download = `broken-items-${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${brokenItems.length} row(s)`);
  };

  const runBulkExtract = async (mode) => {
    setBulkExtracting(true);
    try {
      const token = localStorage.getItem("access_token");
      const payload = mode === "retry_partial" ? { mode: "retry_partial" } : { ids: selectedIds };
      const resp = await axios.post(`${API}/admin/bulk-extract-metadata`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { job_id, queued, message } = resp.data || {};
      if (!job_id || !queued) {
        toast.info(message || "No items to process.", { duration: 5000 });
      } else {
        toast.success(
          `Metadata extraction running in the background for ${queued} item(s) — you can keep working. Progress appears next to the search bar.`,
          { duration: 8000 },
        );
        setExtractJob({ job_id, status: "running", processed: 0, errors: 0, total: queued });
        setSelectedIds([]);
      }
    } catch (error) {
      console.error("Bulk extract error:", error);
      const detail = error.response?.data?.detail || "Error during bulk extraction";
      toast.error(detail);
    } finally {
      setBulkExtracting(false);
    }
  };

  const fetchPartialCount = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.get(`${API}/admin/partial-extraction-count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPartialCount(resp.data?.count || 0);
    } catch {
      /* non-blocking */
    }
  };

  const refreshExtractJob = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.get(`${API}/admin/bulk-extract-status/latest`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return resp.data || null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    (async () => {
      fetchPartialCount();
      const job = await refreshExtractJob();
      if (job && job.status === "running") setExtractJob(job);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!extractJob || extractJob.status !== "running") return undefined;
    const id = setInterval(async () => {
      const job = await refreshExtractJob();
      if (!job) return;
      setExtractJob(job);
      if (job.status === "completed") {
        toast.success(
          `Metadata extraction finished: ${job.processed}/${job.total} succeeded${job.errors > 0 ? `, ${job.errors} failed` : ""}`,
          { duration: 10000 },
        );
        fetchItems();
        fetchPartialCount();
      }
    }, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extractJob?.status]);


  const toggleSort = (field) => {
    let newOrder;
    if (sortBy === field) {
      newOrder = sortOrder === "desc" ? "asc" : "desc";
    } else {
      newOrder = field === "created_at" ? "desc" : "asc";
    }
    setSortBy(field);
    setSortOrder(newOrder);
    fetchItems({ page: 1, sort_by: field, sort_order: newOrder });
  };

  const filteredItems = items.filter((item) => {
    // Text search filter
    if (itemSearch.trim()) {
      const term = normalizeText(itemSearch);
      const title = normalizeText(item.title);
      const authors = normalizeText((item.authors || []).join(", "));
      const tags = normalizeText((item.tags || []).join(", "));
      const category = normalizeText(item.category || "new");
      const matchesText =
        title.includes(term) || authors.includes(term) || tags.includes(term) || category.includes(term);
      if (!matchesText) return false;
    }

    // Similarity filter
    if (similarityFilter === "not_checked") {
      if (item.similarity_flag) return false;
    } else if (similarityFilter !== "all") {
      const flag = item.similarity_flag === "very_low" ? "low" : item.similarity_flag;
      if (similarityFilter === "high" && flag !== "high") return false;
      if (similarityFilter === "medium" && flag !== "medium") return false;
      if (similarityFilter === "low" && flag !== "low") return false;
    }

    // Category filter
    if (categoryFilter !== "all") {
      const cat = (item.category || "").toLowerCase();
      if (categoryFilter === "article") {
        // Mirror backend: treat missing category as article
        if (cat && cat !== "article") return false;
      } else if (cat !== categoryFilter.toLowerCase()) {
        return false;
      }
    }

    // Organization filter
    if (canManageOrg && orgFilter !== "all") {
      const itemOrg = normalizeOrg(item.organization);
      if (itemOrg.toLowerCase() !== orgFilter.toLowerCase()) return false;
    }

    return true;
  });

  const sortedItems = filteredItems.sort((a, b) => {
    const orderFactor = sortOrder === "asc" ? 1 : -1;

    if (sortBy === "created_at") {
      const getTs = (item) => {
        if (item.created_at) {
          const ts = Date.parse(item.created_at);
          return Number.isNaN(ts) ? 0 : ts;
        }
        return 0;
      };

      const ta = getTs(a);
      const tb = getTs(b);

      if (ta < tb) return -1 * orderFactor;
      if (ta > tb) return 1 * orderFactor;
      // tie-breaker: fall back to title so order still visibly changes
      const at = (a.title || "").toLowerCase();
      const bt = (b.title || "").toLowerCase();
      if (at < bt) return -1 * orderFactor;
      if (at > bt) return 1 * orderFactor;
      return 0;
    }

    if (sortBy === "title") {
      // Fully rely on backend ordering for title sort
      return 0;
    }

    const getKey = (item) => {
      switch (sortBy) {
        case "year":
          return item.year || 0;
        default:
          return 0;
      }
    };

    const aVal = getKey(a);
    const bVal = getKey(b);

    if (aVal < bVal) return -1 * orderFactor;
    if (aVal > bVal) return 1 * orderFactor;
    return 0;
  });

  const pagedItems = items;

  const openEditDialog = async (item) => {
    setEditingItem(item);
    // Fetch full item data (list view excludes abstract for performance)
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.get(`${API}/items/${item.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const fullItem = resp.data || item;
      setEditingItem({ ...item, ...fullItem });
      setEditForm({
        title: fullItem.title || "",
        authors: (fullItem.authors || []).join(", "),
        abstract: fullItem.abstract || "",
        year: fullItem.year ? String(fullItem.year) : "",
        language: fullItem.language || "en",
        category: fullItem.category || "new",
        tags: (fullItem.tags || []).join(", "),
        access_level: fullItem.access_level || "public",
        is_paid: Boolean(fullItem.is_paid),
        price: fullItem.price != null ? String(fullItem.price) : "",
        organization: fullItem.organization || "Public",
      });
    } catch (err) {
      // Fallback to list data if fetch fails
      setEditForm({
        title: item.title || "",
        authors: (item.authors || []).join(", "),
        abstract: item.abstract || "",
        year: item.year ? String(item.year) : "",
        language: item.language || "en",
        category: item.category || "new",
        tags: (item.tags || []).join(", "),
        access_level: item.access_level || "public",
        is_paid: Boolean(item.is_paid),
        price: item.price != null ? String(item.price) : "",
        organization: item.organization || "Public",
      });
    }
    setCustomCategoryMode(false);
    setShowEditDialog(true);
  };

  // Deep-link support: /admin/items?edit=<itemId> opens that item's editor directly
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const editId = params.get("edit");
    if (!editId) return;
    (async () => {
      try {
        const token = localStorage.getItem("access_token");
        const resp = await axios.get(`${API}/items/${editId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (resp.data) openEditDialog(resp.data);
      } catch (err) {
        toast.error("Could not open this item for editing");
      } finally {
        window.history.replaceState({}, "", "/admin/items");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEditChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingItem) return;

    try {
      const token = localStorage.getItem("access_token");
      const payload = {
        title: editForm.title,
        authors: editForm.authors
          .split(",")
          .map((a) => a.trim())
          .filter(Boolean),
        abstract: editForm.abstract || null,
        year: editForm.year ? parseInt(editForm.year, 10) : null,
        language: editForm.language,
        category: editForm.category || "new",
        tags: editForm.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        access_level: editForm.access_level,
        is_paid: Boolean(editForm.is_paid),
        price: editForm.is_paid && editForm.price ? parseFloat(editForm.price) : null,
      };

      if (canManageOrg && editForm.organization) {
        payload.organization = editForm.organization;
      }

      await axios.patch(`${API}/items/${editingItem.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Item updated successfully");
      setShowEditDialog(false);
      setEditingItem(null);
      await fetchItems();
    } catch (error) {
      console.error("Edit error:", error);
      toast.error("Error updating item");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top nav */}
      <nav className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-xl font-semibold" style={{ fontFamily: "Fraunces, serif" }}>
              Admin Dashboard
            </h1>
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => navigate("/")}
                variant="ghost"
                className="text-white hover:text-white hover:bg-slate-800"
              >
                Public Site
              </Button>
              <Button
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="text-white hover:text-white hover:bg-slate-800"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <AdminSubNav user={user} activeTab="items" />

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <DragDropUpload onUploadComplete={fetchItems} />

        <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
          <h2 className="text-2xl font-bold" style={{ fontFamily: "Fraunces, serif" }}>
            Manage Items
          </h2>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              type="text"
              value={itemSearch}
              onChange={(e) => {
                const val = e.target.value;
                setItemSearch(val);
                debouncedSearch(val);
              }}
              placeholder="Search items by title, authors, category, tags..."
              className="w-72"
              data-testid="admin-items-search"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-xs"
              onClick={() => {
                setCategoryFilter("all");
                setOrgFilter("all");
                setSimilarityFilter("all");
                setLanguageFilter("all");
                setFileTypeFilter("all");
                setYearFrom("");
                setYearTo("");
                setPriceMin("");
                setPriceMax("");
                setItemSearch("");
                fetchItems({ page: 1, category: "all", organization: "all", search: "", language: "all", file_type: "all", year_from: "", year_to: "", price_min: "", price_max: "" });
              }}
              data-testid="clear-filters-btn"
            >
              Clear filters
            </Button>

            {/* OCR queue status — kept visible so admins don't have to open the menu to see it */}
            {ocrQueueStatus && (ocrQueueStatus.queue.pending + ocrQueueStatus.queue.processing) > 0 && (
              <span
                className="text-xs px-2 py-1 rounded bg-purple-100 text-purple-800 border border-purple-200 font-medium whitespace-nowrap"
                data-testid="ocr-queue-pill"
              >
                OCR Queue: {ocrQueueStatus.queue.pending + ocrQueueStatus.queue.processing} running
              </span>
            )}

            {/* Live progress of the background metadata extraction job */}
            {extractJob && extractJob.status === "running" && (
              <span
                className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800 border border-blue-200 font-medium whitespace-nowrap"
                data-testid="bulk-extract-progress-pill"
              >
                <Loader2 className="w-3 h-3 mr-1 animate-spin inline" />
                Extracting metadata: {(extractJob.processed || 0) + (extractJob.errors || 0)}/{extractJob.total}
              </span>
            )}

            {/* One-click retry for all items flagged 'Partial extraction' */}
            {partialCount > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-xs bg-orange-50 border-orange-200 text-orange-800 hover:bg-orange-100"
                disabled={bulkExtracting || (extractJob && extractJob.status === "running")}
                onClick={() => runBulkExtract("retry_partial")}
                data-testid="fix-partial-extractions-btn"
                title="Re-run full text + metadata extraction in the background (no time limit) for every item flagged 'Partial extraction'"
              >
                <Wand2 className="w-3 h-3 mr-1" /> Fix Partial Extractions ({partialCount})
              </Button>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  data-testid="admin-actions-menu-trigger"
                >
                  Actions <ChevronDown className="w-3 h-3 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">
                  Find
                </DropdownMenuLabel>
                <DropdownMenuItem
                  onClick={() => { setShowDuplicatesDialog(true); setDupesMode("checksum"); loadDuplicateClusters("checksum"); }}
                  data-testid="menu-find-duplicates"
                  className="text-red-700"
                >
                  Find Duplicates
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => { setShowBrokenDialog(true); loadBrokenFiles(brokenMinSize); }}
                  data-testid="menu-find-broken"
                  className="text-orange-700"
                >
                  Find Broken Files
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => { setShowDiagnoseDialog(true); setDiagnoseResult(null); setDiagnoseError(""); }}
                  data-testid="menu-diagnose-duplicate"
                  className="text-blue-700"
                >
                  Diagnose &quot;Duplicate file&quot; error
                </DropdownMenuItem>

                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">
                  Maintenance
                </DropdownMenuLabel>
                <DropdownMenuItem
                  disabled={repairing}
                  onClick={runRepairFiles}
                  data-testid="menu-repair-files"
                >
                  {repairing ? <><Loader2 className="w-3 h-3 mr-2 animate-spin" /> Repairing…</> : "Repair Files"}
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={backfilling}
                  onClick={runBackfillTokens}
                  data-testid="menu-optimize-similarity"
                >
                  {backfilling ? <><Loader2 className="w-3 h-3 mr-2 animate-spin" /> Optimizing…</> : "Optimize Similarity"}
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={reextracting}
                  onClick={runReextractFilenameTitles}
                  data-testid="menu-fix-filename-titles"
                  className="text-amber-800"
                >
                  {reextracting ? <><Loader2 className="w-3 h-3 mr-2 animate-spin" /> Re-extracting…</> : "Fix Filename Titles"}
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={enqueuingOcr}
                  onClick={runEnqueueOcr}
                  data-testid="menu-queue-ocr"
                  className="text-purple-700"
                >
                  {enqueuingOcr ? <><Loader2 className="w-3 h-3 mr-2 animate-spin" /> Queuing…</> : "Queue OCR for scanned PDFs"}
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={convertingToPdf}
                  onClick={runConvertToPdfBatch}
                  data-testid="menu-convert-to-pdf"
                  className="text-orange-700"
                >
                  {convertingToPdf ? (
                    <><Loader2 className="w-3 h-3 mr-2 animate-spin" /> Converting…</>
                  ) : (
                    <>Convert non-PDFs to PDF{convertPdfStatus?.convertible > 0 ? ` (${convertPdfStatus.convertible})` : ""}</>
                  )}
                </DropdownMenuItem>

                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">
                  View
                </DropdownMenuLabel>
                <DropdownMenuItem
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  data-testid="menu-toggle-advanced-filters"
                >
                  {showAdvancedFilters ? "Hide Filters" : "More Filters"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4 mb-4 text-sm text-slate-600">
          {/* Category filter */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Category:</span>
            <Select
              value={categoryFilter}
              onValueChange={(value) => {
                setCategoryFilter(value);
                fetchItems({ page: 1, category: value });
              }}
            >
              <SelectTrigger className="h-8 w-36 text-xs">
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="article">Article</SelectItem>
                <SelectItem value="thesis">Thesis</SelectItem>
                <SelectItem value="course">Course</SelectItem>
                <SelectItem value="report">Report</SelectItem>
                <SelectItem value="book">Book</SelectItem>
                <SelectItem value="note">Note</SelectItem>
                <SelectItem value="preprint">Preprint</SelectItem>
                {customCategories.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Organization filter (superadmin/2RA only) */}
          {canManageOrg && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Organization:</span>
              <Select
                value={orgFilter}
                onValueChange={(value) => {
                  setOrgFilter(value);
                  fetchItems({ page: 1, organization: value });
                }}
              >
                <SelectTrigger className="h-8 w-40 text-xs">
                  <SelectValue placeholder="All organizations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {orgOptions.map((org) => (
                    <SelectItem key={org} value={org}>
                      {org}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Similarity filter */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Similarity:</span>
            <Select value={similarityFilter} onValueChange={(value) => setSimilarityFilter(value)}>
              <SelectTrigger className="h-8 w-44 text-xs" data-testid="similarity-filter">
                <SelectValue placeholder="Similarity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Similarity</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="not_checked">Not checked</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Sort controls */}
          <div className="flex items-center gap-2 ml-auto text-xs">
            <span className="text-slate-500">Sort by:</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={`px-2 py-1 rounded ${
                sortBy === "title" ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
              onClick={() => toggleSort("title")}
            >
              Title {sortBy === "title" && (sortOrder === "asc" ? "↑" : "↓")}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={`px-2 py-1 rounded ${
                sortBy === "year" ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
              onClick={() => toggleSort("year")}
            >
              Year {sortBy === "year" && (sortOrder === "asc" ? "↑" : "↓")}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={`px-2 py-1 rounded ${
                sortBy === "created_at"
                  ? "bg-orange-500 text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
              onClick={() => toggleSort("created_at")}
            >
              Date Added {sortBy === "created_at" && (sortOrder === "asc" ? "↑" : "↓")}
            </Button>
          </div>
        </div>

        {/* Advanced filters row */}
        {showAdvancedFilters && (
          <div className="flex flex-wrap items-end gap-4 mb-4 p-4 bg-white border border-slate-200 rounded-md" data-testid="advanced-filters-panel">
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Language:</span>
              <Select
                value={languageFilter}
                onValueChange={(value) => {
                  setLanguageFilter(value);
                  fetchItems({ page: 1, language: value });
                }}
              >
                <SelectTrigger className="h-8 w-32 text-xs" data-testid="filter-language">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {availableFilters.languages.map((l) => (
                    <SelectItem key={l} value={l}>{LANGUAGE_LABELS[l] || l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">File Type:</span>
              <Select
                value={fileTypeFilter}
                onValueChange={(value) => {
                  setFileTypeFilter(value);
                  fetchItems({ page: 1, file_type: value });
                }}
              >
                <SelectTrigger className="h-8 w-28 text-xs" data-testid="filter-file-type">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {availableFilters.file_types.map((f) => (
                    <SelectItem key={f} value={f}>{f}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Year:</span>
              <Input
                type="number"
                placeholder="From"
                value={yearFrom}
                onChange={(e) => setYearFrom(e.target.value)}
                className="h-8 w-20 text-xs"
                data-testid="filter-year-from"
              />
              <span className="text-xs text-slate-400">-</span>
              <Input
                type="number"
                placeholder="To"
                value={yearTo}
                onChange={(e) => setYearTo(e.target.value)}
                className="h-8 w-20 text-xs"
                data-testid="filter-year-to"
              />
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Price:</span>
              <Input
                type="number"
                placeholder="Min"
                value={priceMin}
                onChange={(e) => setPriceMin(e.target.value)}
                className="h-8 w-20 text-xs"
                data-testid="filter-price-min"
              />
              <span className="text-xs text-slate-400">-</span>
              <Input
                type="number"
                placeholder="Max"
                value={priceMax}
                onChange={(e) => setPriceMax(e.target.value)}
                className="h-8 w-20 text-xs"
                data-testid="filter-price-max"
              />
            </div>

            <Button
              type="button"
              size="sm"
              className="h-8 bg-orange-500 hover:bg-orange-600 text-white text-xs"
              onClick={() => fetchItems({ page: 1 })}
              data-testid="apply-advanced-filters"
            >
              Apply
            </Button>
          </div>
        )}

        {loading && (
          <div className="text-center py-12">Loading...</div>
        )}

        {!loading && (
          <div className="bg-white border border-slate-200 rounded-md">
            {/* Bulk actions toolbar */}
            {selectedIds.length > 0 && (
              <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-slate-50 text-xs text-slate-700">
                <div>{selectedIds.length} item(s) selected</div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedIds([])}
                  >
                    Clear selection
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={handleBulkDelete}
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Delete selected
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
                    disabled={bulkExtracting}
                    onClick={() => runBulkExtract("selected")}
                    data-testid="extract-selected-btn"
                    title="Extract title, authors, year, tags, language, and abstract for selected items. Auto-runs OCR for scanned PDFs."
                  >
                    {bulkExtracting ? (
                      <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Extracting...</>
                    ) : (
                      <><Wand2 className="w-3 h-3 mr-1" /> Extract Metadata</>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100"
                    onClick={() => openAddToCollection(selectedIds)}
                    data-testid="add-to-collection-bulk-btn"
                    title="Add the selected items to one of your collections"
                  >
                    <FolderPlus className="w-3 h-3 mr-1" /> Add to Collection
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="bg-orange-50 border-orange-200 text-orange-800 hover:bg-orange-100"
                    onClick={() => {
                      setBulkCategoryValue("new");
                      setBulkCategoryCustomMode(false);
                      setShowBulkCategoryDialog(true);
                    }}
                    data-testid="bulk-category-btn"
                    title="Move the selected items to another category"
                  >
                    <Tag className="w-3 h-3 mr-1" /> Change Category
                  </Button>
                </div>
              </div>
            )}

            <Dialog open={showBulkCategoryDialog} onOpenChange={(open) => { setShowBulkCategoryDialog(open); if (!open) setBulkCategoryCustomMode(false); }}>
              <DialogContent className="max-w-md" data-testid="bulk-category-dialog">
                <DialogHeader>
                  <DialogTitle>Change category for {selectedIds.length} item(s)</DialogTitle>
                </DialogHeader>
                {bulkCategoryCustomMode ? (
                  <div className="flex gap-1.5">
                    <Input
                      value={bulkCategoryValue}
                      onChange={(e) => setBulkCategoryValue(e.target.value)}
                      placeholder="e.g. Unikol-info"
                      maxLength={60}
                      autoFocus
                      className="h-9 text-sm"
                      data-testid="bulk-category-custom-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-9 shrink-0 text-xs"
                      onClick={() => { setBulkCategoryCustomMode(false); setBulkCategoryValue("new"); }}
                    >
                      List
                    </Button>
                  </div>
                ) : (
                  <Select
                    value={bulkCategoryValue}
                    onValueChange={(value) => {
                      if (value === "__custom__") {
                        setBulkCategoryCustomMode(true);
                        setBulkCategoryValue("");
                        return;
                      }
                      setBulkCategoryValue(value);
                    }}
                  >
                    <SelectTrigger className="h-9 w-full text-sm" data-testid="bulk-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="article">Article/Paper</SelectItem>
                      <SelectItem value="course">Course</SelectItem>
                      <SelectItem value="report">Report</SelectItem>
                      <SelectItem value="thesis">Thesis</SelectItem>
                      <SelectItem value="book">Book</SelectItem>
                      <SelectItem value="note">Note</SelectItem>
                      <SelectItem value="preprint">Preprint</SelectItem>
                      {customCategories.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                      <SelectItem value="__custom__">+ Custom category…</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                <div className="flex justify-end gap-2 mt-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => setShowBulkCategoryDialog(false)}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    className="bg-orange-500 hover:bg-orange-600"
                    disabled={bulkCategorySaving || !(bulkCategoryValue || "").trim()}
                    onClick={applyBulkCategory}
                    data-testid="bulk-category-apply"
                  >
                    {bulkCategorySaving ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Applying…</> : "Apply"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <div className="w-full overflow-x-auto">
              <table className="min-w-full table-fixed">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider w-10">
                      <input
                        type="checkbox"
                        className="h-3 w-3 rounded border-slate-300"
                        onChange={handleToggleSelectAll}
                        checked={
                          pagedItems.length > 0 &&
                          pagedItems.every((it) => selectedIds.includes(it.id))
                        }
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                      Title / Authors
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                      Organization
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                      Year
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                      Similarity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                      File
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {pagedItems.map((item) => {
                    // "Recently auto-extracted" = metadata_extracted_at within last 60 seconds
                    const extractedAt = item.metadata_extracted_at;
                    let recentlyExtracted = false;
                    if (extractedAt) {
                      const t = new Date(extractedAt).getTime();
                      if (!Number.isNaN(t) && Date.now() - t < 60 * 1000) {
                        recentlyExtracted = true;
                      }
                    }
                    const rowClass = recentlyExtracted
                      ? "bg-emerald-50/60 hover:bg-emerald-100/60 border-l-4 border-l-emerald-400 transition-colors"
                      : "hover:bg-slate-50 transition-colors";
                    return (
                    <tr key={item.id} className={rowClass} data-recently-extracted={recentlyExtracted || undefined}>
                      <td className="px-3 py-4 align-top">
                        <input
                          type="checkbox"
                          className="h-3 w-3 rounded border-slate-300"
                          checked={selectedIds.includes(item.id)}
                          onChange={() => handleToggleSelectOne(item.id)}
                        />
                      </td>
                      <td className="px-6 py-4 align-top">
                        <div className="font-medium text-slate-900 uppercase truncate max-w-xs flex items-center gap-2" title={item.title}>
                          <span className="truncate">{item.title}</span>
                          {recentlyExtracted && (
                            <span
                              className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200 flex-shrink-0"
                              title={`Auto-extracted at ${new Date(extractedAt).toLocaleString()}`}
                              data-testid="recently-extracted-badge"
                            >
                              <Wand2 className="w-2.5 h-2.5 mr-0.5" />
                              Just extracted
                            </span>
                          )}
                          {item.ocr_status === "pending" && (
                            <span
                              className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 flex-shrink-0"
                              title="Waiting for OCR worker to process this scanned PDF"
                              data-testid="ocr-pending-badge"
                            >
                              OCR pending
                            </span>
                          )}
                          {item.ocr_status === "processing" && (
                            <span
                              className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200 flex-shrink-0"
                              title="OCR worker is currently processing this file"
                              data-testid="ocr-processing-badge"
                            >
                              <Loader2 className="w-2.5 h-2.5 mr-0.5 animate-spin" />
                              OCR running
                            </span>
                          )}
                          {item.ocr_status === "failed" && (
                            <span
                              className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200 flex-shrink-0"
                              title={item.ocr_last_error || "OCR failed for this file"}
                              data-testid="ocr-failed-badge"
                            >
                              OCR failed
                            </span>
                          )}
                          {item.extraction_partial && (
                            <span
                              className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 border border-orange-200 flex-shrink-0"
                              title={item.extraction_note || "Metadata was extracted from a subset of pages — review manually"}
                              data-testid="extraction-partial-badge"
                            >
                              Partial extraction
                            </span>
                          )}
                        </div>
                        <div
                          className="text-xs text-slate-600 truncate max-w-xs"
                          title={item.authors?.join(", ") || "-"}
                        >
                          {item.authors?.join(", ") || "-"}
                        </div>
                        {item.content_snippet && (
                          <div className="mt-1 text-xs text-slate-500 italic max-w-xs line-clamp-2" data-testid="content-snippet">
                            <span className="text-orange-600 font-medium not-italic">Content match: </span>
                            <HighlightSnippet text={item.content_snippet} term={itemSearch} />
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-xs text-slate-700">
                        {CATEGORY_LABELS[(item.category || "article").toLowerCase()] || item.category || "Article/Paper"}
                      </td>
                      <td className="px-4 py-4 text-xs text-slate-700">
                        {normalizeOrg(item.organization)}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">{item.year || "-"}</td>
                      <td className="px-4 py-4 text-xs">
                        {item.similarity_flag ? (
                          <span className="inline-flex items-center rounded-full px-2 py-0.5 font-medium border">
                            {(() => {
                              const rawScore = typeof item.similarity_score === "number" ? item.similarity_score : null;
                              const pct = rawScore != null ? Math.round(rawScore * 100) : null;
                              const label =
                                item.similarity_flag === "high"
                                  ? "High"
                                  : item.similarity_flag === "medium"
                                  ? "Medium"
                                  : "Low";
                              return pct != null ? `${label} (${pct}%)` : label;
                            })()}
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="ml-2 h-4 w-4"
                              title="Re-check similarity"
                              disabled={checkingSimilarityId === item.id}
                              onClick={() => runSimilarityCheck(item)}
                            >
                              {checkingSimilarityId === item.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <RefreshCw className="w-3 h-3" />
                              )}
                            </Button>
                          </span>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="text-xs"
                            disabled={checkingSimilarityId === item.id}
                            onClick={() => runSimilarityCheck(item)}
                            data-testid={`check-similarity-btn-${item.id}`}
                          >
                            {checkingSimilarityId === item.id ? (
                              <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Checking...</>
                            ) : (
                              "Check similarity"
                            )}
                          </Button>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {item.has_file ? (
                          <span className="text-green-600 text-sm">Yes</span>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/items/${item.id}`)}
                            data-testid={`upload-file-btn-${item.id}`}
                          >
                            <Upload className="w-3 h-3 mr-1" />
                            Upload
                          </Button>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right space-x-2">
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="text-slate-600 hover:text-orange-600"
                          title="Add to a collection"
                          aria-label="Add to a collection"
                          onClick={() => openAddToCollection([item.id])}
                          data-testid={`add-to-collection-btn-${item.id}`}
                        >
                          <FolderPlus className="w-4 h-4" />
                        </Button>
                        {item.has_file && (
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="text-slate-600 hover:text-slate-900"
                            title="Generate abstract with AI"
                            aria-label="Generate abstract with AI"
                            onClick={async () => {
                              const t = toast.loading("Generating abstract... (OCR may take 30–90s for scanned PDFs)");
                              try {
                                const token = localStorage.getItem("access_token");
                                const resp = await axios.post(
                                  `${API}/admin/items/${item.id}/generate-abstract`,
                                  {},
                                  {
                                    headers: { Authorization: `Bearer ${token}` },
                                    timeout: 300000,
                                  },
                                );
                                const abstract = resp.data?.abstract || "";
                                toast.dismiss(t);
                                toast.success("Abstract generated from file text");
                                if (editingItem && editingItem.id === item.id) {
                                  handleEditChange("abstract", abstract);
                                }
                              } catch (err) {
                                toast.dismiss(t);
                                console.error("Row generate abstract error:", err);
                                const msg =
                                  err.response?.data?.detail ||
                                  (err.code === "ECONNABORTED"
                                    ? "Request timed out — try again."
                                    : "Error generating abstract");
                                toast.error(msg, { duration: 8000 });
                              }
                            }}
                          >
                            <Wand2 className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/items/${item.id}`)}
                          data-testid={`view-item-btn-${item.id}`}
                        >
                          View
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditDialog(item)}
                          data-testid={`edit-item-btn-${item.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteItem(item.id)}
                          data-testid={`delete-item-btn-${item.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
              {pagedItems.length === 0 && (
                <div className="text-center py-12 text-slate-600">No items match the current filters.</div>
              )}
            </div>
          </div>
        )}
        {!loading && (totalItems || 0) > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-3 text-xs text-slate-600 border-t border-slate-200">
            <div>
              Page {page} of {Math.max(1, Math.ceil(totalItems / PAGE_SIZE))} · {totalItems} items
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => {
                  if (page > 1) {
                    fetchItems({ page: page - 1 });
                  }
                }}
              >
                Previous
              </Button>
              <div className="flex items-center gap-1">
                <Input
                  type="number"
                  min={1}
                  max={Math.max(1, Math.ceil(totalItems / PAGE_SIZE))}
                  value={page}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val)) setPage(val);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const maxPage = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
                      const target = Math.min(Math.max(1, page), maxPage);
                      fetchItems({ page: target });
                    }
                  }}
                  onBlur={() => {
                    const maxPage = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
                    const target = Math.min(Math.max(1, page), maxPage);
                    if (target !== page) setPage(target);
                    fetchItems({ page: target });
                  }}
                  className="w-16 h-8 text-center text-xs"
                  data-testid="page-input"
                />
                <span className="text-slate-400">/ {Math.max(1, Math.ceil(totalItems / PAGE_SIZE))}</span>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page >= Math.ceil(totalItems / PAGE_SIZE)}
                onClick={() => {
                  const maxPage = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
                  if (page < maxPage) {
                    fetchItems({ page: page + 1 });
                  }
                }}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Edit Item Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-xl w-full max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Item</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1 flex items-center justify-between">
                <span>Title</span>
                {editingItem?.has_file && (
                  <Button
                    type="button"
                    size="xs"
                    variant="outline"
                    className="text-xs px-2 py-0 h-6"
                    disabled={extractingMeta}
                    data-testid="extract-title-btn"
                    onClick={async () => {
                      setExtractingMeta(true);
                      try {
                        const token = localStorage.getItem("access_token");
                        const resp = await axios.post(
                          `${API}/items/${editingItem.id}/extract-title`,
                          {},
                          { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 },
                        );
                        const { suggested_title, suggested_author, suggested_year, suggested_tags, suggested_language, suggested_abstract } = resp.data || {};
                        const langNames = { en: "English", fr: "French", es: "Spanish", de: "German", pt: "Portuguese", it: "Italian", ru: "Russian", ar: "Arabic" };
                        let msg = "";
                        if (suggested_title) msg += `Title: "${suggested_title}"`;
                        if (suggested_author) msg += `${msg ? "\n" : ""}Authors: "${suggested_author}"`;
                        if (suggested_year) msg += `${msg ? "\n" : ""}Year: ${suggested_year}`;
                        if (suggested_language) msg += `${msg ? "\n" : ""}Language: ${langNames[suggested_language] || suggested_language}`;
                        if (suggested_tags?.length) msg += `${msg ? "\n" : ""}Tags: ${suggested_tags.join(", ")}`;
                        if (suggested_abstract) msg += `${msg ? "\n" : ""}Abstract: "${suggested_abstract.substring(0, 100)}..."`;
                        if (!msg) {
                          toast.error("Could not extract metadata from the file.");
                          return;
                        }
                        const accept = window.confirm(`Extracted from file:\n\n${msg}\n\nApply these changes?`);
                        if (accept) {
                          if (suggested_title) handleEditChange("title", suggested_title);
                          if (suggested_author) handleEditChange("authors", suggested_author);
                          if (suggested_year) handleEditChange("year", String(suggested_year));
                          if (suggested_language) handleEditChange("language", suggested_language);
                          if (suggested_tags?.length) handleEditChange("tags", suggested_tags.join(", "));
                          if (suggested_abstract) handleEditChange("abstract", suggested_abstract);
                          toast.success("Metadata updated!");
                        }
                      } catch (err) {
                        console.error("Extract title error:", err);
                        const msg = err.response?.data?.detail || "Error extracting metadata";
                        toast.error(msg);
                      } finally {
                        setExtractingMeta(false);
                      }
                    }}
                  >
                    {extractingMeta ? (
                      <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Extracting...</>
                    ) : (
                      "Extract from File"
                    )}
                  </Button>
                )}
              </label>
              <Input
                value={editForm.title}
                onChange={(e) => handleEditChange("title", e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Authors (comma-separated)</label>
              <Input
                value={editForm.authors}
                onChange={(e) => handleEditChange("authors", e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 flex items-center justify-between">
                <span>Abstract</span>
                {editingItem?.has_file && (
                  <Button
                    type="button"
                    size="xs"
                    variant="outline"
                    className="text-xs px-2 py-0 h-6"
                    onClick={async () => {
                      const t = toast.loading("Generating abstract... (OCR may take 30–90s for scanned PDFs)");
                      try {
                        const token = localStorage.getItem("access_token");
                        const resp = await axios.post(
                          `${API}/admin/items/${editingItem.id}/generate-abstract`,
                          {},
                          {
                            headers: { Authorization: `Bearer ${token}` },
                            timeout: 300000, // 5 min — OCR + AI can be slow
                          },
                        );
                        const abstract = resp.data?.abstract || "";
                        handleEditChange("abstract", abstract);
                        toast.dismiss(t);
                        toast.success("Abstract generated");
                      } catch (err) {
                        toast.dismiss(t);
                        console.error("Generate abstract error:", err);
                        const msg =
                          err.response?.data?.detail ||
                          (err.code === "ECONNABORTED"
                            ? "Request timed out. The file may be very large or the AI is slow — try again."
                            : "Error generating abstract");
                        toast.error(msg, { duration: 8000 });
                      }
                    }}
                  >
                    Use AI
                  </Button>
                )}
              </label>
              <Textarea
                value={editForm.abstract}
                onChange={(e) => handleEditChange("abstract", e.target.value)}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Year</label>
                <Input
                  type="number"
                  value={editForm.year}
                  onChange={(e) => handleEditChange("year", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Language</label>
                <Select
                  value={editForm.language || "en"}
                  onValueChange={(value) => handleEditChange("language", value)}
                >
                  <SelectTrigger className="h-9 w-full text-sm">
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGE_OPTIONS.map((code) => (
                      <SelectItem key={code} value={code}>
                        {LANGUAGE_LABELS[code] || code}
                      </SelectItem>
                    ))}
                    {editForm.language && !LANGUAGE_OPTIONS.includes(editForm.language) && (
                      <SelectItem value={editForm.language}>{editForm.language}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Category</label>
                {customCategoryMode ? (
                  <div className="flex gap-1.5">
                    <Input
                      value={editForm.category || ""}
                      onChange={(e) => handleEditChange("category", e.target.value)}
                      placeholder="e.g. Unikol-info"
                      maxLength={60}
                      autoFocus
                      className="h-9 text-sm"
                      data-testid="edit-custom-category-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-9 shrink-0 text-xs"
                      onClick={() => {
                        setCustomCategoryMode(false);
                        handleEditChange("category", editingItem?.category || "new");
                      }}
                      data-testid="edit-custom-category-back"
                    >
                      List
                    </Button>
                  </div>
                ) : (
                <Select
                  value={editForm.category || "new"}
                  onValueChange={(value) => {
                    if (value === "__custom__") {
                      setCustomCategoryMode(true);
                      handleEditChange("category", "");
                      return;
                    }
                    handleEditChange("category", value);
                  }}
                >
                  <SelectTrigger className="h-9 w-full text-sm" data-testid="edit-category-trigger">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="article">Article/Paper</SelectItem>
                    <SelectItem value="course">Course</SelectItem>
                    <SelectItem value="report">Report</SelectItem>
                    <SelectItem value="thesis">Thesis</SelectItem>
                    <SelectItem value="book">Book</SelectItem>
                    <SelectItem value="note">Note</SelectItem>
                    <SelectItem value="preprint">Preprint</SelectItem>
                    {[...new Set([
                      ...customCategories,
                      ...(editForm.category && !STANDARD_CATEGORIES.includes((editForm.category || "").toLowerCase()) ? [editForm.category] : []),
                    ])].map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                    <SelectItem value="__custom__" data-testid="edit-category-custom-option">
                      + Custom category…
                    </SelectItem>
                  </SelectContent>
                </Select>
                )}
              </div>
              {canManageOrg && (
                <div>
                  <label className="block text-sm font-medium mb-1">Organization</label>
                  <Select
                    value={editForm.organization}
                    onValueChange={(value) => handleEditChange("organization", value)}
                  >
                    <SelectTrigger className="h-9 w-full text-sm">
                      <SelectValue placeholder="Select organization" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Public">Public</SelectItem>
                      {orgOptions.filter((org) => org.toLowerCase() !== "public").map((org) => (
                        <SelectItem key={org} value={org}>
                          {org}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            {!canManageOrg && editingItem && (
              <div className="text-xs text-slate-500">
                Organization: {(editingItem.organization || "Public").toUpperCase()} (cannot change)
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Access Level</label>
                <Select
                  value={editForm.access_level}
                  onValueChange={(value) => handleEditChange("access_level", value)}
                >
                  <SelectTrigger className="h-9 w-full text-sm">
                    <SelectValue placeholder="Select access" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">Public</SelectItem>
                    <SelectItem value="restricted">Restricted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="is_paid"
                  type="checkbox"
                  checked={editForm.is_paid}
                  onChange={(e) => handleEditChange("is_paid", e.target.checked)}
                />
                <label htmlFor="is_paid" className="text-sm">
                  Paid item
                </label>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Price</label>
                <Input
                  type="number"
                  step="0.01"
                  value={editForm.price}
                  onChange={(e) => handleEditChange("price", e.target.value)}
                  disabled={!editForm.is_paid}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Tags (comma-separated)</label>
                <Input
                  value={editForm.tags}
                  onChange={(e) => handleEditChange("tags", e.target.value)}
                />
              </div>
            </div>
            {!canManageOrg && editingItem && (
              <div className="text-xs text-slate-500">
                Organization: {(editingItem.organization || "Public").toUpperCase()} (cannot change)
              </div>
            )}
            {/* Similarity check results */}
            {editingItem?.has_file && (
              <div className="border border-slate-200 rounded-md p-3 bg-slate-50" data-testid="editor-similarity-section">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Similarity check</span>
                  <Button
                    type="button"
                    size="xs"
                    variant="outline"
                    className="text-xs px-2 py-0 h-6"
                    disabled={checkingSimilarityId === editingItem.id}
                    data-testid="editor-run-similarity-btn"
                    onClick={() => {
                      setShowEditDialog(false);
                      runSimilarityCheck(editingItem);
                    }}
                  >
                    {editingItem.similarity_checked_at ? "Re-check" : "Run check"}
                  </Button>
                </div>
                {editingItem.similarity_checked_at ? (
                  <div className="mt-2 space-y-2 text-xs text-slate-600">
                    <div data-testid="editor-similarity-score">
                      Score:{" "}
                      <span className={`font-semibold ${
                        Math.round((editingItem.similarity_score || 0) * 100) >= 30
                          ? "text-red-600"
                          : Math.round((editingItem.similarity_score || 0) * 100) >= 11
                            ? "text-amber-600"
                            : "text-green-600"
                      }`}>
                        {Math.round((editingItem.similarity_score || 0) * 100)}%
                      </span>
                      <span className="text-slate-400">
                        {" "}· checked {new Date(editingItem.similarity_checked_at).toLocaleDateString()}
                      </span>
                    </div>
                    {editingItem.similarity_matched_item_id ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="truncate max-w-[220px]" title={editingItem.similarity_matched_title || ""}>
                          Closest match: <strong>{editingItem.similarity_matched_title || "Untitled"}</strong>
                        </span>
                        <Button
                          type="button"
                          size="xs"
                          variant="outline"
                          className="text-xs px-2 py-0 h-6"
                          data-testid="editor-view-comparison-btn"
                          onClick={() => {
                            setShowEditDialog(false);
                            navigate(`/admin/items/${editingItem.id}/similarity`, {
                              state: {
                                auditedItem: editingItem,
                                similarity: {
                                  percent: Math.round((editingItem.similarity_score || 0) * 100),
                                  matched_item_id: editingItem.similarity_matched_item_id,
                                },
                              },
                            });
                          }}
                        >
                          View comparison
                        </Button>
                        <Button
                          type="button"
                          size="xs"
                          variant="outline"
                          className="text-xs px-2 py-0 h-6"
                          data-testid="editor-view-matched-item-btn"
                          onClick={() => window.open(`/items/${editingItem.similarity_matched_item_id}`, "_blank")}
                        >
                          View matched item
                        </Button>
                      </div>
                    ) : (
                      <div className="text-slate-500">No matching document found in the catalogue.</div>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 mt-1">Not checked yet.</p>
                )}
              </div>
            )}
            <div className="flex justify-end gap-3 mt-4">
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">Save changes</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add to Collection Dialog */}
      <Dialog open={collectionDialogOpen} onOpenChange={(open) => {
        setCollectionDialogOpen(open);
        if (!open) {
          setShowNewCollectionInput(false);
          setNewCollectionName("");
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Add {collectionDialogItemIds.length === 1 ? "item" : `${collectionDialogItemIds.length} items`} to a collection
            </DialogTitle>
          </DialogHeader>
          {collectionsLoading ? (
            <div className="py-8 text-center text-slate-500 text-sm">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
              Loading your collections...
            </div>
          ) : (
            <div className="space-y-3">
              {myCollections.length > 0 && (
                <div className="max-h-72 overflow-y-auto -mx-2 px-2 space-y-1">
                  {myCollections.map((c) => {
                    const alreadyHasAll = collectionDialogItemIds.every((id) => (c.items || []).includes(id));
                    return (
                      <button
                        key={c.id}
                        type="button"
                        disabled={addingToCollection || alreadyHasAll}
                        onClick={() => addItemsToCollection(c.id)}
                        className={`w-full text-left px-3 py-2 rounded-md border transition ${
                          alreadyHasAll
                            ? "bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed"
                            : "bg-white border-slate-200 hover:border-orange-300 hover:bg-orange-50"
                        }`}
                        data-testid={`pick-collection-${c.id}`}
                      >
                        <div className="font-medium text-sm text-slate-900 truncate">{c.name}</div>
                        <div className="text-xs text-slate-500 flex items-center gap-2">
                          <span>{c.item_count ?? (c.items?.length || 0)} items</span>
                          <span>•</span>
                          <span>{c.is_public ? "Public" : "Private"}</span>
                          {alreadyHasAll && <span className="text-amber-600 ml-auto">Already added</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Create new inline */}
              <div className="pt-2 border-t border-slate-200">
                {!showNewCollectionInput ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full text-orange-700 border-orange-200 bg-orange-50 hover:bg-orange-100"
                    onClick={() => setShowNewCollectionInput(true)}
                    data-testid="create-new-collection-btn"
                  >
                    <FolderPlus className="w-4 h-4 mr-1" />
                    {myCollections.length === 0 ? "Create your first collection" : "Create new collection"}
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      autoFocus
                      placeholder="Collection name (e.g. Cobalt Research 2024)"
                      value={newCollectionName}
                      onChange={(e) => setNewCollectionName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          createAndAddToCollection();
                        }
                        if (e.key === "Escape") {
                          setShowNewCollectionInput(false);
                          setNewCollectionName("");
                        }
                      }}
                      className="h-9 text-sm"
                      disabled={creatingCollection}
                      data-testid="new-collection-name-input"
                    />
                    <Button
                      type="button"
                      size="sm"
                      disabled={creatingCollection || !newCollectionName.trim()}
                      onClick={createAndAddToCollection}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                      data-testid="confirm-create-collection-btn"
                    >
                      {creatingCollection ? <Loader2 className="w-3 h-3 animate-spin" /> : "Create & Add"}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== Find Duplicates Dialog ===== */}
      <Dialog open={showDuplicatesDialog} onOpenChange={setShowDuplicatesDialog}>
        <DialogContent className="max-w-4xl w-full max-h-[88vh] overflow-y-auto" data-testid="duplicates-dialog">
          <DialogHeader>
            <DialogTitle>Find Duplicate Files</DialogTitle>
          </DialogHeader>

          {/* Mode tabs */}
          <div className="flex flex-wrap items-center gap-2 mb-4 border-b border-slate-200 pb-3">
            <button
              onClick={() => { setDupesMode("checksum"); loadDuplicateClusters("checksum"); }}
              data-testid="dupe-tab-checksum"
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                dupesMode === "checksum"
                  ? "bg-red-600 text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              Byte-identical (SHA)
            </button>
            <button
              onClick={() => { setDupesMode("near"); loadDuplicateClusters("near", dupesThreshold); }}
              data-testid="dupe-tab-near"
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                dupesMode === "near"
                  ? "bg-amber-600 text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              Near-duplicate (text similarity)
            </button>
            {dupesMode === "near" && (
              <div className="ml-auto flex items-center gap-2 text-xs text-slate-600">
                <span>Threshold:</span>
                <select
                  value={dupesThreshold}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    setDupesThreshold(v);
                    loadDuplicateClusters("near", v);
                  }}
                  data-testid="dupe-threshold-select"
                  className="border border-slate-300 rounded px-2 py-1 text-xs bg-white"
                >
                  <option value="0.95">95% (very strict)</option>
                  <option value="0.90">90%</option>
                  <option value="0.85">85% (recommended)</option>
                  <option value="0.75">75% (loose)</option>
                  <option value="0.60">60% (very loose)</option>
                </select>
              </div>
            )}
          </div>

          {dupesLoading ? (
            <div className="py-10 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              {dupesMode === "near"
                ? "Scanning library for near-duplicate text…"
                : "Scanning library for byte-identical files…"}
            </div>
          ) : dupesClusters.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-500">
              {dupesMode === "near"
                ? `No near-duplicates found at this threshold (scanned ${dupesScanned} items).`
                : "No byte-identical duplicates found. Every file is unique."}
            </div>
          ) : (
            <div className="space-y-4">
              <div className={`flex flex-wrap items-center justify-between gap-3 p-3 rounded text-sm ${
                dupesMode === "near" ? "bg-amber-50 border border-amber-200 text-amber-900" : "bg-red-50 border border-red-200 text-red-900"
              }`}>
                <div className="flex-1 min-w-[260px]">
                  Found <strong>{dupesClusters.length}</strong> cluster(s) totaling{" "}
                  <strong>{dupesTotal}</strong> extra cop{dupesTotal === 1 ? "y" : "ies"}.
                  {dupesMode === "near" && <> Scanned <strong>{dupesScanned}</strong> items.</>}
                  {" "}The <em>oldest</em> item in each cluster is kept by default.
                </div>
                <label className="flex items-center gap-2 text-xs font-medium select-none cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dupesMergeBeforeDelete}
                    onChange={(e) => setDupesMergeBeforeDelete(e.target.checked)}
                    data-testid="dupe-merge-toggle"
                    className="w-4 h-4"
                  />
                  <span title="Before deleting, fill any missing fields on the kept item (abstract, year, authors, tags, translations, file) from the duplicates.">
                    Auto-merge metadata into keeper
                  </span>
                </label>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={deleteSelectedDuplicates}
                  disabled={dupesDeleting}
                  data-testid="delete-selected-dupes-btn"
                >
                  {dupesDeleting ? (
                    <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Working…</>
                  ) : (
                    <>
                      {dupesMergeBeforeDelete ? "Merge & Delete" : "Delete"}{" "}
                      ({Object.values(dupesSelected).filter(Boolean).length})
                    </>
                  )}
                </Button>
              </div>

              {dupesClusters.map((cluster, ci) => (
                <div
                  key={cluster.checksum || `near-${ci}`}
                  className="border border-slate-200 rounded-lg overflow-hidden"
                  data-testid={`dupe-cluster-${(cluster.checksum || `near-${ci}`).slice(0, 8)}`}
                >
                  <div className="bg-slate-50 px-3 py-2 text-xs font-mono text-slate-600 border-b border-slate-200 flex flex-wrap gap-3">
                    <span><strong>{cluster.count}</strong> copies</span>
                    {cluster.size && <><span>•</span><span>{(cluster.size / 1024).toFixed(1)} KB each</span></>}
                    {typeof cluster.score === "number" && (
                      <><span>•</span><span>Similarity: {(cluster.score * 100).toFixed(1)}%</span></>
                    )}
                    {cluster.checksum && (
                      <><span>•</span><span>SHA: {cluster.checksum.slice(0, 16)}…</span></>
                    )}
                  </div>
                  <table className="w-full text-sm">
                    <tbody>
                      {cluster.items.map((it, idx) => (
                        <tr key={it.item_id} className={idx === 0 ? "bg-green-50" : ""}>
                          <td className="px-3 py-2 w-10 align-top">
                            <input
                              type="checkbox"
                              checked={!!dupesSelected[it.item_id]}
                              onChange={(e) => setDupesSelected((s) => ({ ...s, [it.item_id]: e.target.checked }))}
                              disabled={idx === 0 && !dupesSelected[it.item_id]}
                              data-testid={`dupe-checkbox-${it.item_id.slice(0, 8)}`}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <div className="font-medium text-slate-900 truncate max-w-[400px]">
                              {it.title || "(untitled)"}
                              {idx === 0 && (
                                <span className="ml-2 inline-block text-[10px] uppercase tracking-wider text-green-700 bg-green-200 px-2 py-0.5 rounded">
                                  Oldest — keep
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">
                              {it.created_at?.slice(0, 10) || "?"} • {it.organization || "—"}
                              {it.year && <> • {it.year}</>}
                              {it.filename && <> • {it.filename}</>}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right w-24">
                            <a
                              href={`/items/${it.item_id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs text-blue-600 hover:underline"
                            >
                              View ↗
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== Find Broken/Empty Files Dialog ===== */}
      <Dialog open={showBrokenDialog} onOpenChange={setShowBrokenDialog}>
        <DialogContent className="max-w-4xl w-full max-h-[88vh] overflow-y-auto" data-testid="broken-files-dialog">
          <DialogHeader>
            <DialogTitle>Find Broken / Empty Files</DialogTitle>
          </DialogHeader>

          <div className="flex flex-wrap items-center gap-3 mb-4 pb-3 border-b border-slate-200 text-xs text-slate-600">
            <span>Detects items whose file is missing, 0 bytes, suspiciously small, or empty-checksum.</span>
            <label className="ml-auto flex items-center gap-2">
              Min file size:
              <select
                value={brokenMinSize}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  setBrokenMinSize(v);
                  loadBrokenFiles(v);
                }}
                data-testid="broken-minsize-select"
                className="border border-slate-300 rounded px-2 py-1 bg-white"
              >
                <option value={0}>Only missing / 0 bytes</option>
                <option value={500}>500 B</option>
                <option value={2048}>2 KB</option>
                <option value={10240}>10 KB</option>
                <option value={102400}>100 KB</option>
              </select>
            </label>
          </div>

          {brokenLoading ? (
            <div className="py-10 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Scanning for broken files…
            </div>
          ) : brokenItems.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-500">
              No broken or empty files found at this threshold.
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3 p-3 bg-orange-50 border border-orange-200 rounded text-sm text-orange-900">
                <div className="flex-1 min-w-[260px]">
                  Found <strong>{brokenItems.length}</strong> broken or empty item(s).
                  All are pre-selected for deletion — untick any you want to spare.
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={exportBrokenAsCSV}
                  disabled={brokenItems.length === 0}
                  className="bg-slate-50 border-slate-300 text-slate-700 hover:bg-slate-100"
                  data-testid="export-broken-csv-btn"
                  title="Download the full list as CSV (opens in Excel, Numbers, Sheets)."
                >
                  Export CSV
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={openReuploadPicker}
                  disabled={brokenReuploading || brokenDeleting}
                  className="bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
                  data-testid="reupload-broken-btn"
                  title="Pick files from your computer — they'll be matched to the selected broken items by title and attached, preserving titles/tags/IDs."
                >
                  {brokenReuploading ? (
                    <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Re-uploading…</>
                  ) : (
                    <>Re-upload Selected</>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={deleteBrokenFiles}
                  disabled={brokenDeleting || brokenReuploading}
                  data-testid="delete-broken-btn"
                >
                  {brokenDeleting ? (
                    <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Moving to Recycle Bin…</>
                  ) : (
                    <>Delete {Object.values(brokenSelected).filter(Boolean).length}</>
                  )}
                </Button>
                <input
                  ref={brokenReuploadInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.docx,.txt,.epub,.pptx,.doc,.ppt"
                  onChange={handleReuploadFiles}
                  className="hidden"
                />
              </div>

              <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
                <thead className="bg-slate-50 text-xs text-slate-600">
                  <tr>
                    <th className="px-3 py-2 w-10"></th>
                    <th className="px-3 py-2 text-left">Title</th>
                    <th className="px-3 py-2 text-left">Reason</th>
                    <th className="px-3 py-2 text-left">Size</th>
                    <th className="px-3 py-2 text-left">Created</th>
                    <th className="px-3 py-2 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {brokenItems.map((it) => (
                    <tr key={it.item_id} className="border-t border-slate-100">
                      <td className="px-3 py-2 align-top">
                        <input
                          type="checkbox"
                          checked={!!brokenSelected[it.item_id]}
                          onChange={(e) => setBrokenSelected((s) => ({ ...s, [it.item_id]: e.target.checked }))}
                          data-testid={`broken-checkbox-${it.item_id.slice(0, 8)}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-900 truncate max-w-[350px]">{it.title || "(untitled)"}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {it.organization || "—"} • {it.category || "—"}
                          {it.filename && <> • {it.filename}</>}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <span className="inline-block px-2 py-0.5 rounded bg-orange-100 text-orange-800 font-medium">
                          {it.reason}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600 font-mono">{it.file_size} B</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{it.created_at?.slice(0, 10) || "?"}</td>
                      <td className="px-3 py-2 text-right">
                        <a
                          href={`/items/${it.item_id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-blue-600 hover:underline"
                        >
                          View ↗
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Diagnose duplicate dialog */}
      <Dialog open={showDiagnoseDialog} onOpenChange={setShowDiagnoseDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Diagnose &quot;Duplicate file&quot; error</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-xs text-slate-600">
              <b>Title match:</b> paste the filename you&apos;re trying to upload. We&apos;ll show every existing item with that title.<br/>
              <b>Content match (SHA256):</b> pick the actual file. We hash it and show every existing item whose stored file has the same byte content.
            </div>
            <div className="space-y-2 border border-slate-200 rounded-md p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">By title</div>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={diagnoseTitle}
                  onChange={(e) => setDiagnoseTitle(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") runDiagnoseDuplicate(); }}
                  placeholder="e.g. 300-000-131-460.pdf"
                  className="flex-1 text-sm"
                  data-testid="diagnose-title-input"
                />
                <Button
                  size="sm"
                  onClick={runDiagnoseDuplicate}
                  disabled={diagnoseLoading}
                  data-testid="diagnose-run-btn"
                >
                  {diagnoseLoading ? <><Loader2 className="w-3 h-3 mr-2 animate-spin" /> Scanning…</> : "Diagnose by title"}
                </Button>
              </div>
            </div>
            <div className="space-y-2 border border-slate-200 rounded-md p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">By content (SHA256)</div>
              <div className="flex gap-2">
                <input
                  type="file"
                  accept=".pdf,.docx,.txt,.epub,.pptx,.doc,.ppt"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) runDiagnoseDuplicateByContent(f);
                  }}
                  className="flex-1 text-sm border border-slate-300 rounded-md p-2"
                  data-testid="diagnose-content-input"
                  disabled={diagnoseLoading}
                />
              </div>
              <div className="text-[10px] text-slate-500">
                File is hashed locally then sent to the server for lookup. Nothing is permanently stored.
              </div>
            </div>

            {diagnoseError && (
              <div className="p-3 rounded-md bg-red-50 text-red-700 text-xs border border-red-200">
                Error: {diagnoseError}
              </div>
            )}

            {diagnoseResult && diagnoseResult.mode === "content" && (
              <div className="space-y-3">
                <div className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-md p-3 space-y-1">
                  <div><b>File:</b> {diagnoseResult.filename} ({(diagnoseResult.size_bytes / 1024).toFixed(1)} KB)</div>
                  <div className="font-mono text-[10px] break-all"><b>SHA256:</b> {diagnoseResult.sha256}</div>
                  <div>
                    <b>Existing items with this content:</b>
                    <span className="text-emerald-700"> active: {diagnoseResult.active_count || 0}</span>,
                    <span className="text-slate-500"> recycled: {diagnoseResult.recycled_count || 0}</span>,
                    <span className="text-red-500"> orphan: {diagnoseResult.orphaned_count || 0}</span>
                  </div>
                </div>
                {diagnoseResult.matches && diagnoseResult.matches.length > 0 && (
                  <div className="border border-slate-200 rounded-md overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider text-[10px]">
                        <tr>
                          <th className="px-2 py-1.5 text-left">Item</th>
                          <th className="px-2 py-1.5 text-left">Org</th>
                          <th className="px-2 py-1.5 text-left">Status</th>
                          <th className="px-2 py-1.5 text-left">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {diagnoseResult.matches.map((m) => {
                          const isActive = m.item_found && !m.is_deleted;
                          const isRecycled = m.item_found && m.is_deleted;
                          return (
                            <tr key={m.item_id} className={isActive ? "bg-amber-50" : "bg-slate-50"}>
                              <td className="px-2 py-1.5">
                                <div className="font-medium text-slate-900 truncate max-w-xs" title={m.title || m.item_id}>
                                  {m.title || <span className="italic text-red-600">[no parent item — orphan]</span>}
                                </div>
                                <div className="text-[10px] text-slate-500 font-mono">{m.item_id}</div>
                                {m.filename_in_db && <div className="text-[10px] text-slate-400">stored as: {m.filename_in_db}</div>}
                              </td>
                              <td className="px-2 py-1.5">{m.organization || (m.item_found ? "Public" : "—")}</td>
                              <td className="px-2 py-1.5">
                                {isActive && <span className="text-amber-700 font-semibold">Active (blocking)</span>}
                                {isRecycled && <span className="text-slate-500">Recycled (OK)</span>}
                                {!m.item_found && <span className="text-red-500">Orphan (will auto-clean)</span>}
                              </td>
                              <td className="px-2 py-1.5 space-x-1">
                                {isActive && (
                                  <>
                                    <Button size="sm" variant="outline" className="text-xs h-7 px-2 text-orange-700 border-orange-300" onClick={() => deleteBlockerItem(m.item_id)} data-testid={`diagnose-c-recycle-${m.item_id}`}>
                                      Move to bin
                                    </Button>
                                    <Button size="sm" variant="outline" className="text-xs h-7 px-2 text-red-700 border-red-300" onClick={() => purgeBlockerCompletely(m.item_id)} data-testid={`diagnose-c-purge-${m.item_id}`}>
                                      Purge
                                    </Button>
                                  </>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {diagnoseResult.verdict && (
                  <div className={`p-3 rounded-md text-xs border ${diagnoseResult.active_count > 0 ? "bg-red-50 text-red-700 border-red-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                    {diagnoseResult.verdict}
                  </div>
                )}
              </div>
            )}

            {diagnoseResult && diagnoseResult.mode === "title" && (
              <div className="space-y-3">
                <div className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-md p-3">
                  <div><b>Normalized title searched:</b> &quot;{diagnoseResult.normalized}&quot;</div>
                  <div className="mt-1">
                    <b>Total matches:</b> {diagnoseResult.total_matches} —
                    <span className="text-emerald-700"> active: {diagnoseResult.active_count}</span>,
                    <span className="text-slate-500"> in recycle bin: {diagnoseResult.recycled_count}</span>
                  </div>
                </div>

                {diagnoseResult.total_matches === 0 && (
                  <div className="p-3 rounded-md bg-emerald-50 text-emerald-700 text-xs border border-emerald-200">
                    No matching items found. The duplicate error must be coming from another path —
                    please copy any blocking item id from the actual error toast and share it.
                  </div>
                )}

                {diagnoseResult.matches && diagnoseResult.matches.length > 0 && (
                  <div className="border border-slate-200 rounded-md overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider text-[10px]">
                        <tr>
                          <th className="px-2 py-1.5 text-left">Title</th>
                          <th className="px-2 py-1.5 text-left">Org</th>
                          <th className="px-2 py-1.5 text-left">Status</th>
                          <th className="px-2 py-1.5 text-left">Has File</th>
                          <th className="px-2 py-1.5 text-left">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {diagnoseResult.matches.map((m) => {
                          const isActive = !m.is_deleted;
                          const reallyHasFile = m.file_row != null || (m.file_count || 0) > 0;
                          return (
                            <tr key={m.id} className={isActive ? "bg-amber-50" : "bg-slate-50"}>
                              <td className="px-2 py-1.5">
                                <div className="font-medium text-slate-900 truncate max-w-xs" title={m.title}>{m.title}</div>
                                <div className="text-[10px] text-slate-500 font-mono">{m.id}</div>
                              </td>
                              <td className="px-2 py-1.5">{m.organization || "Public"}</td>
                              <td className="px-2 py-1.5">
                                {isActive
                                  ? <span className="text-amber-700 font-semibold">Active (blocking)</span>
                                  : <span className="text-slate-500">Recycled</span>}
                              </td>
                              <td className="px-2 py-1.5">
                                {reallyHasFile
                                  ? <span className="text-red-700">Yes ({m.file_row?.size || m.file_size || "?"} bytes)</span>
                                  : <span className="text-emerald-700">No (ghost)</span>}
                              </td>
                              <td className="px-2 py-1.5 space-x-1">
                                {isActive && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-xs h-7 px-2 text-orange-700 border-orange-300"
                                    onClick={() => deleteBlockerItem(m.id)}
                                    data-testid={`diagnose-recycle-${m.id}`}
                                  >
                                    Move to bin
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-xs h-7 px-2 text-red-700 border-red-300"
                                  onClick={() => purgeBlockerCompletely(m.id)}
                                  data-testid={`diagnose-purge-${m.id}`}
                                >
                                  Purge permanently
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {diagnoseResult.advice && (
                  <div className="p-3 rounded-md bg-blue-50 text-blue-700 text-xs border border-blue-200">
                    <b>Tip:</b> {diagnoseResult.advice}
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default AdminItems;
